<template>
  <section class="page">
    <div class="page__header">
      <div class="page__title">
        <SharedBreadcrumbs :breadcrumbs />

        <FaqSearch />
      </div>

      <h1 class="typography__headline1">Добавление и&nbsp;редактирование клиента</h1>
    </div>

    <div class="page__content">
      <FaqArticlesAside :menu />

      <FaqArticlesContent>
        <div class="base-content-block">
          <FaqArticlesBlockHeading
            id="kak-popast"
            class="typography__headline2"
          >
            Как попасть
          </FaqArticlesBlockHeading>

          <div class="block-small">
            <p>Давайте добавим клиента в&nbsp;ваш проект.</p>

            <p>
              В&nbsp;меню слева перейдите в&nbsp;раздел &laquo;Клиенты&raquo;. Если у&nbsp;вас еще нет
              клиентов&nbsp;&mdash; на&nbsp;появившейся странице нажмите &laquo;Добавить клиента&raquo;.
            </p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/dobavleniye-i-redaktirovaniye-klienta-1.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <p>Если клиенты уже есть&nbsp;&mdash; на&nbsp;появившейся странице нажмите &laquo;Добавить клиента&raquo;.</p>

          <FaqArticlesImageModalButton
            image-source="/images/faq/dobavleniye-i-redaktirovaniye-klienta-2.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />
        </div>

        <div class="base-content-block">
          <FaqArticlesBlockHeading
            id="dobavleniye-klienta"
            class="typography__headline2"
          >
            Добавление клиента
          </FaqArticlesBlockHeading>

          <div class="block-small">
            <p>
              В&nbsp;основной информации заполните обязательные поля&nbsp;&mdash; фамилию, имя и&nbsp;телефон клиента.
            </p>

            <p>Остальные данные клиент также может добавить сам в&nbsp;редактировании профиля на&nbsp;сайте.</p>

            <p>При включении черного списка, клиент будет игнорироваться в&nbsp;чате.</p>

            <p>Ниже добавьте адрес доставки клиента для заказов, если требуется.</p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/dobavleniye-i-redaktirovaniye-klienta-3.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <p>
            Как заполните все нужные данные клиента, нажмите &laquo;Добавить&raquo; в&nbsp;самом низу страницы. После
            успешного добавления увидите в&nbsp;правом верхнем углу уведомление.
          </p>

          <FaqArticlesImageModalButton
            image-source="/images/faq/dobavleniye-i-redaktirovaniye-klienta-4.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />
        </div>

        <div class="base-content-block">
          <FaqArticlesBlockHeading
            id="redaktirovaniye-klienta"
            class="typography__headline2"
          >
            Редактирование клиента
          </FaqArticlesBlockHeading>

          <div class="block-small">
            <p>
              После добавления клиента, вы&nbsp;сможете найти его в&nbsp;<BaseLink
                class="ui-link-secondary"
                to="/faq/articles/spisok-klientov-i-ikh-dannye"
                >списке клиентов</BaseLink
              >.
            </p>

            <p>Отсюда можно быстро перейти к&nbsp;редактированию по&nbsp;иконке карандаша справа.</p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/dobavleniye-i-redaktirovaniye-klienta-5.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <p>
            С&nbsp;добавлением клиента разобрались. Самое время перейти к&nbsp;изучению
            <BaseLink
              class="ui-link-secondary"
              to="/faq/articles/spisok-klientov-i-ikh-dannye"
              >списка клиентов и&nbsp;просмотра их&nbsp;данных</BaseLink
            >.
          </p>
        </div>
      </FaqArticlesContent>
    </div>

    <div class="page__related">
      <h2
        class="typography__headline2"
        v-html="t('pages.faq.otherArticles')"
      />

      <FaqArticlesList :articles="relatedArticles" />
    </div>
  </section>

  <LazyFaqArticlesImageModal
    v-if="isModalOpen(MODAL.FaqArticlesImage) && activeImageSource"
    :src="activeImageSource"
    @close="() => (activeImageSource = null)"
  />
</template>

<script lang="ts" setup>
const { t } = useI18n();
const { isModalOpen } = useModalStore();
const activeImageSource = ref<null | string>(null);

const breadcrumbs = [
  {
    name: t("pages.faq.title"),
    url: "/faq",
  },
  {
    name: "Клиенты",
    url: "/faq/categories/klienty",
  },
  {
    name: "Добавление и&nbsp;редактирование клиента",
  },
];

const menu = [
  {
    name: "Как&nbsp;попасть",
    url: "kak-popast",
  },
  {
    name: "Добавление&nbsp;клиента",
    url: "dobavleniye-klienta",
  },
  {
    name: "Редактирование&nbsp;клиента",
    url: "redaktirovaniye-klienta",
  },
];

const relatedArticles = [
  {
    name: "Список клиентов и&nbsp;их данные",
    slug: "spisok-klientov-i-ikh-dannye",
  },
  {
    name: "Чат с&nbsp;клиентом",
    slug: "chat-s-klientom",
  },
];

useSeoMeta({
  title: "Добавление и редактирование клиента",
});
</script>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  row-gap: 40px;
}

.page__header {
  display: flex;
  flex-direction: column;
  row-gap: 40px;
}

.page__title {
  display: flex;
  flex-direction: column;
  row-gap: 24px;
}

.page__content {
  display: flex;
  flex-direction: column;
  gap: 40px;
}

@media (--xl) {
  .page__content {
    flex-direction: row-reverse;
    justify-content: start;
    align-items: start;
  }
}

.page__related {
  display: flex;
  flex-direction: column;
  row-gap: 24px;
}
</style>
